import { useEffect, useState } from 'react';
import {
  Search, X, Check, Loader2,
} from 'lucide-react';
import * as chatApi from '../../../api/chat';
import type { Conversation, EligibleUser } from '../../../api/chat';
import Avatar from './Avatar';

function NewConvModal({
  onClose, onCreated, currentUserId,
}: {
  onClose: () => void; onCreated: (c: Conversation) => void; currentUserId: string;
}) {
  const [mode, setMode] = useState<'dm' | 'group'>('dm');
  const [selected, setSelected] = useState<string[]>([]);
  const [groupName, setGroupName] = useState('');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState<EligibleUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    chatApi.getEligibleUsers()
      .then(users => setEligibleUsers(users))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = eligibleUsers.filter(u =>
    u.id !== currentUserId &&
    (u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.designation?.toLowerCase().includes(search.toLowerCase()))
  );

  const toggle = (id: string) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const submit = async () => {
    if (!selected.length) return;
    setSubmitting(true);
    try {
      const conv = mode === 'dm'
        ? await chatApi.getOrCreateDm(selected[0])
        : await chatApi.createConversation({
          type: 'group',
          name: groupName.trim() || 'New Group',
          member_ids: selected,
        });
      onCreated(conv);
      onClose();
    } catch {} finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-bold text-lg text-gray-900">New Conversation</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
            {(['dm', 'group'] as const).map(m => (
              <button key={m} onClick={() => setMode(m)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                  mode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}>
                {m === 'dm' ? '💬 Direct Message' : '👥 Group Chat'}
              </button>
            ))}
          </div>

          {mode === 'group' && (
            <input
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="Group name (optional)"
              value={groupName} onChange={e => setGroupName(e.target.value)} />
          )}

          <div className="relative">
            <Search size={14} className="absolute left-3.5 top-3 text-gray-400" />
            <input
              className="w-full pl-9 pr-4 border border-gray-200 rounded-xl py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="Search people…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <div className="max-h-56 overflow-y-auto space-y-0.5 -mx-1">
            {loading && (
              <div className="text-center py-5 text-sm text-gray-400 flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading users…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-5 text-sm text-gray-400">No users found</div>
            )}
            {filtered.map(u => {
              const isSelected = selected.includes(u.id);
              return (
                <label key={u.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-colors ${
                    isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}>
                  <input
                    type={mode === 'dm' ? 'radio' : 'checkbox'}
                    name="user"
                    checked={isSelected}
                    onChange={() => mode === 'dm' ? setSelected([u.id]) : toggle(u.id)}
                    className="hidden" />
                  <Avatar name={u.name} size={9} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-gray-800 truncate">{u.name}</div>
                    <div className="text-xs text-gray-400 truncate">{u.designation || u.email || ''}</div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                  }`}>
                    {isSelected && <Check size={11} className="text-white" />}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <div className="px-5 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={submit} disabled={submitting || !selected.length}
            className="w-full bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors shadow-sm">
            {submitting
              ? 'Creating…'
              : mode === 'dm' ? '💬 Open Chat' : `👥 Create Group${selected.length > 0 ? ` (${selected.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewConvModal;
